import {useRef, useState} from "react";
import {Alert, Checkbox, FormControlLabel} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import VLFModal from "@/components/Modal";
import MainActions from "@/components/MainActions";
import {ProviderMatchesAPI} from "@/api/scouting_statistics";
import {importProvider} from "@/utils/scoutingStatisticsData";
import "./index.scss";
const formatDate = (iso) => iso?.split("-").reverse().join("/") || "—";
export default function WyscoutImport({
	player,
	onImported,
	handleNotification,
	canWrite = false,
}) {
	const input = useRef(null);
	const ticket = useRef(0);
	const [file, setFile] = useState(null),
		[preview, setPreview] = useState(null),
		[busy, setBusy] = useState(false),
		[error, setError] = useState(""),
		[mismatch, setMismatch] = useState(false),
		[skipped, setSkipped] = useState(false),
		[conflict, setConflict] = useState(false);
	const review = async (selected) => {
		const current = ++ticket.current;
		setBusy(true);
		setPreview(null);
		setError("");
		setConflict(false);
		setMismatch(false);
		setSkipped(false);
		try {
			const result = await ProviderMatchesAPI.preview(player.id, selected);
			if (ticket.current === current) setPreview(result);
		} catch (e) {
			if (ticket.current === current)
				setError(
					e.response?.data?.error ||
						e.message ||
						"Impossibile verificare il file.",
				);
		} finally {
			if (ticket.current === current) setBusy(false);
		}
	};
	const handleFile = (e) => {
		const selected = e.target.files?.[0];
		e.target.value = "";
		if (!selected) return;
		setFile(selected);
		review(selected);
	};
	const confirm = async () => {
		setBusy(true);
		setError("");
		try {
			const result = await importProvider(player.id, file, {
				version: preview.version,
				previewToken: preview.previewToken,
				acknowledgePlayerMismatch: mismatch,
				acknowledgeSkippedRows: skipped,
			});
			handleNotification?.(
				result.matches.length + " partite Wyscout salvate sul server.",
				"success",
			);
			setFile(null);
			setPreview(null);
			onImported?.();
		} catch (e) {
			setError(
				e.response?.data?.error || e.message || "Importazione non riuscita.",
			);
			if (e.response?.status === 409) setConflict(true);
		} finally {
			setBusy(false);
		}
	};
	const allowed =
		!!preview &&
		!conflict &&
		!busy &&
		canWrite &&
		(preview.fileNameMatchesPlayer || mismatch) &&
		(!preview.skippedRows || skipped);
	return (
		<>
			{canWrite && (
				<MainActions
					actions={[
						{
							icon: <UploadFileIcon />,
							label: "Importa export Wyscout",
							title: "Importa export Wyscout",
							disabled: busy,
							onClick: () => input.current?.click(),
						},
					]}
				/>
			)}
			<input
				ref={input}
				type="file"
				accept=".xlsx"
				aria-label="Export Wyscout XLSX"
				hidden
				data-testid="wyscout-file"
				disabled={!canWrite || busy}
				onChange={handleFile}
			/>
			{file && (
				<VLFModal
					open
					title="Anteprima import Wyscout"
					confirmLabel="Conferma importazione"
					confirm={confirm}
					disabled={!allowed}
					close={() => {
						if (!busy) {
							ticket.current++;
							setFile(null);
							setPreview(null);
							setError("");
						}
					}}
					content={
						<div className="wyscout-preview">
							{busy && <p>Verifica e salvataggio sul server…</p>}
							{error && <Alert severity="error">{error}</Alert>}
							{((!preview && !busy) || conflict) && (
								<MainActions
									actions={[
										{
											label: "Ripeti anteprima",
											disabled: busy || !canWrite,
											onClick: () => review(file),
										},
									]}
								/>
							)}
							{preview && (
								<>
									<dl>
										<dt>Giocatore</dt>
										<dd>
											{[player.lastName, player.firstName]
												.filter(Boolean)
												.join(" ")}
										</dd>
										<dt>File</dt>
										<dd>{preview.fileName}</dd>
										<dt>Partite</dt>
										<dd>
											{preview.matchesCount} ({preview.friendlies} amichevoli) ·{" "}
											{preview.minutes.toLocaleString("it-IT")}'
										</dd>
										<dt>Periodo</dt>
										<dd>
											{formatDate(preview.fromDate)} –{" "}
											{formatDate(preview.toDate)}
										</dd>
										<dt>Stagioni nel file</dt>
										<dd>{preview.seasons.join(", ")}</dd>
										<dt>Metriche</dt>
										<dd>{preview.metricsCount}</dd>
										<dt>Righe Wyscout da sostituire</dt>
										<dd>
											{preview.replacesCount}
											{!!preview.replacesSeasons?.length &&
												" · " + preview.replacesSeasons.join(", ")}
										</dd>
										{!!preview.unmappedPositions?.length && (
											<>
												<dt>Posizioni senza ruolo associato</dt>
												<dd>{preview.unmappedPositions.join(", ")}</dd>
											</>
										)}
									</dl>
									{!preview.fileNameMatchesPlayer && (
										<Alert severity="warning">
											Il nome del file non corrisponde al giocatore selezionato.
											<FormControlLabel
												control={
													<Checkbox
														checked={mismatch}
														disabled={busy || conflict}
														onChange={(e) => setMismatch(e.target.checked)}
													/>
												}
												label="Ho verificato che il file appartiene a questo giocatore"
											/>
										</Alert>
									)}
									{!!preview.skippedRows && (
										<Alert severity="warning">
											{preview.skippedRows} righe senza data leggibile saranno
											escluse.
											<FormControlLabel
												control={
													<Checkbox
														checked={skipped}
														disabled={busy || conflict}
														onChange={(e) => setSkipped(e.target.checked)}
													/>
												}
												label="Ho verificato le righe escluse"
											/>
										</Alert>
									)}
									<p>
										Confermando sostituisci tutte le statistiche Wyscout di
										questo giocatore nel club, comprese eventuali stagioni
										assenti dal nuovo file. Gli altri provider e le statistiche
										delle gare registrate restano conservati.
									</p>
								</>
							)}
						</div>
					}
				/>
			)}
		</>
	);
}
